
export type AppType =
  | 'athena-net-user'
  | 'athenista'
  | 'adjacent-services-users'
  | 'patient'
  | 'athena-net-user|adjacent-services-users|athenista'
  | 'adjacent-services-users|athenista'
  | 'patient|adjacent-services-users';

export const CREDENTIAL_TYPES = {
  ANET_USERNAME: 'anetUsername',
  NAMESPACED_ANET_USERNAME: 'namespacedAnetUsername',
  EMAIL: 'email',
  UNKNOWN: 'unknown',
} as const;

export type CredentialType = typeof CREDENTIAL_TYPES[keyof typeof CREDENTIAL_TYPES];

export const APP_TYPES: Readonly<Record<string, AppType>> = {
  ATHENANET_USER: 'athena-net-user',
  ATHENISTA: 'athenista',
  ADJACENT_SERVICES_USERS: 'adjacent-services-users',
  PATIENT: 'patient',
  ADJACENT_AONE_ATHENISTA_AUTH: 'athena-net-user|adjacent-services-users|athenista',
  ADJACENT_ATHENISTA: 'adjacent-services-users|athenista',
  PATIENT_OR_ADJACENT: 'patient|adjacent-services-users',
};


export const ERROR_KEYS = {
  GENERIC_INTERNAL_ERROR: 'general.internal.error',
  PASSWORD_RESET_LAMBDA_ERROR: 'password.reset.lambda.error',
  INVALID_USERNAME_FORMAT: 'invalid.username.format',
  FIDO2_SKIP_ENROLLMENT_ERROR: 'fido2.skip.enrollment.error',
};

export const ERROR_MESSAGES: Record<string, string> = {
  'symantec.security.code.validation.failed': 'Your Security Code is invalid.',
  'symantec.credential.expired.error':
        'Bad credential state or credential is expired. Please contact your practice administrator.',
  'symantec.credential.has.been.disabled':
        'This credential has been disabled. Please contact your practice administrator.',
  'symantec.credential.has.been.locked': 'This credential has been locked. Please contact your practice administrator.',
  'symantec.credential.is.not.yet.enabled':
        'This credential is not yet enabled. Please contact your practice administrator.',
  'symantec.credential.is.not.active': 'This credential is not active. Please contact your practice administrator.',
  [ERROR_KEYS.GENERIC_INTERNAL_ERROR]: "We're sorry, something went wrong. Please try again later.",
  [ERROR_KEYS.PASSWORD_RESET_LAMBDA_ERROR]: "We're unable to process your password reset request at this time. Please try again later.",
  [ERROR_KEYS.INVALID_USERNAME_FORMAT]: 'It looks like an email address or password was entered. Please enter a username to reset your password.',
  [ERROR_KEYS.FIDO2_SKIP_ENROLLMENT_ERROR]: 'There was an error skipping this step. Please try again.',
  'symantec.prompt.internal.error':
        "We're having technical problems right now, and we're working onto fix them. We apologize for this inconvenience. Please try logging in again.",
  'error.field.empty': 'This field cannot be left blank',
};

export const INFO_BANNER_MESSAGE_KEY_MAP: Record<string, any> = {
  LOGOUT: {
    header: 'logout.banner.header',
    description: 'logout.banner.description',
  },
  TIMEDOUT: {
    header: 'timedout.banner.header',
    description: 'timedout.banner.description',
  },
  TIMEOUT: {
    header: 'timedout.banner.header',
    description: 'timedout.banner.description',
  },
  REFRESHTIMEOUT: {
    header: 'refreshtimeout.banner.header',
    description: 'refreshtimeout.banner.description',
  },
  LOGINTIMEDOUT: {
    header: 'logintimedout.banner.header',
    description: 'logintimedout.banner.description',
  },
};

export const INFO_BANNER_MESSAGE_KEY_MAP_V2: Record<string, any> = {
  LOGOUT: {
    DEFAULT: {
      header: 'logout.banner.header',
      description: 'logout.banner.description',
      alertType: 'success',
    },
  },
  TIMEDOUT: {
    DEFAULT: {
      header: 'timedout.banner.header',
      description: 'timedout.banner.description',
      alertType: 'info',
    },
  },
  REFRESHTIMEOUT: {
    DEFAULT: {
      header: 'refreshtimeout.banner.header',
      description: 'refreshtimeout.banner.description',
      alertType: 'info',
    },
  },
  CONCIERGEMODE: {
    DEFAULT: {
      header: 'conciergemode.banner.header',
      description: 'conciergemode.banner.description',
      alertType: 'info',
    },
  },
  SESSIONLOCKED: {
    DEFAULT: {
      header: 'sessionlocked.banner.header',
      description: 'sessionlocked.banner.description',
      alertType: 'info',
    },
  },
  LOGINTIMEDOUT: {
    DEFAULT: {
      header: 'logintimedout.banner.header',
      description: 'logintimedout.banner.description',
      alertType: 'info',
    },
  },
  ATHENISTASSSOWARNING: {
    header: 'athenistasssowarning.banner.header',
    descriptionPart1: 'athenistasssowarning.banner.description.part1',
    descriptionPart2: 'athenistasssowarning.banner.description.part2',
    alertType: 'info',
  },
  ERROR: {
    DEFAULT: {
      header: 'error.default.header',
      description: 'error.default.description',
      alertType: 'critical',
    },
    ACCOUNTEXPIRED: {
      header: 'error.accountexpired.header',
      description: 'error.accountexpired.description',
      alertType: 'critical',
    },
    ACCOUNTBLOCKEDSHELLUSER: {
      header: 'error.accountblockedshelluser.header',
      description: 'error.accountblockedshelluser.description',
      alertType: 'critical',
    },
    ACCOUNTBLOCKED: {
      header: 'error.accountblocked.header',
      description: 'error.accountblocked.description',
      alertType: 'critical',
    },
    TIMEOFDAYRESTRICTED: {
      header: 'error.timeofdayrestricted.header',
      description: 'error.timeofdayrestricted.description',
      alertType: 'info',
    },
    UNKNOWNIDENTITYPROVIDER: {
      header: 'error.unknownidentityprovider.header',
      description: 'error.unknownidentityprovider.description',
      alertType: 'attention',
    },
    EPCSIDPNOTALLOWED: {
      header: 'error.epcsidpnotallowed.header',
      description: 'error.epcsidpnotallowed.description',
      alertType: 'attention',
    },
    UNKNOWNANETUSER: {
      header: 'error.unknownanetuser.header',
      description: 'error.unknownanetuser.description',
      alertType: 'attention',
    },
    WRONGUSER: {
      header: 'error.wronguser.header',
      description: 'error.wronguser.description',
      alertType: 'info',
    },
    ACCOUNTMAPPINGERRORMULTIPLE: {
      header: 'error.accountmappingerrormultiple.header',
      description: 'error.accountmappingerrormultiple.description',
      alertType: 'attention',
    },
    ACCOUNTMAPPINGERROREMPTY: {
      header: 'error.accountmappingerrorempty.header',
      description: 'error.accountmappingerrorempty.description',
      alertType: 'attention',
    },
    AUTOCREATIONERROR: {
      header: 'error.autocreationerror.header',
      description: 'error.autocreationerror.description',
      alertType: 'attention',
    },
    BADTOKEN: {
      header: 'error.badtoken.header',
      description: 'error.badtoken.description',
      alertType: 'critical',
    },
    TRANSIENTERROR: {
      header: 'error.transienterror.header',
      description: 'error.transienterror.description',
      alertType: 'critical',
    },
    ACCESSDENIED: {
      header: 'error.accessdenied.header',
      description: 'error.accessdenied.description',
      alertType: 'critical',
    },
    TEMPORARILYUNAVAILABLE: {
      header: 'error.temporarilyunavailable.header',
      description: 'error.temporarilyunavailable.description',
      alertType: 'attention',
    },
    INVALIDIDENTITYPROVIDER: {
      header: 'error.invalididentityprovider.header',
      description: 'error.invalididentityprovider.description',
      alertType: 'attention',
    },
    TOOMANYREQUESTS: {
      header: 'error.toomanyrequests.header',
      description: 'error.toomanyrequests.description',
      alertType: 'attention',
    },
    NOPRACTICE: {
      header: 'error.nopractice.header',
      description: 'error.nopractice.description',
      alertType: 'attention',
    },
    TERMINALNOTALLOWED: {
      header: 'error.terminalnotallowed.header',
      description: 'error.terminalnotallowed.description',
      alertType: 'attention',
    },
    ACCOUNTBLOCKEDMAXLOGINS: {
      header: 'error.accountblockedmaxlogins.header',
      description: 'error.accountblockedmaxlogins.description',
      alertType: 'critical',
    },
    INVALIDIDENTITYMAPPING: {
      header: 'error.invalididentitymapping.header',
      description: 'error.invalididentitymapping.description',
      alertType: 'attention',
    },
    RESETPASSWORDBLOCKEDUNLOCKEDUSER: {
      header: 'error.resetpasswordblockedunlockeduser.header',
      description: 'error.resetpasswordblockedunlockeduser.description',
      alertType: 'critical',
    },
    RESETPASSWORDBLOCKEDUSER: {
      header: 'error.resetpasswordblockeduser.header',
      description: 'error.resetpasswordblockeduser.description',
      alertType: 'critical',
    },
    AUTHSERVERACCESSDENIED: {
      header: 'error.authserveraccessdenied.header',
      description: 'error.authserveraccessdenied.description',
      alertType: 'critical',
    },
  },
};

export const NON_FUZZY_ANET_USER_DOMAIN = 'managedidentity.platform.athenahealth.com';
export const FUZZY_ENV_MAPPING: Record<string, any> = {
  preview: {
    userNamespace: '.preview.athenahealth.com',
    anetUserDomain: `preview.${NON_FUZZY_ANET_USER_DOMAIN}`,
  },
  slsdemo: {
    userNamespace: '.salesdemo.athenahealth.com',
    anetUserDomain: `salesdemo.${NON_FUZZY_ANET_USER_DOMAIN}`,
  },
  clienttrain: {
    userNamespace: '.clienttrain.athenahealth.com',
    anetUserDomain: `clienttrain.${NON_FUZZY_ANET_USER_DOMAIN}`,
  },
  sandbox: {
    userNamespace: '.sandbox.athenahealth.com',
    anetUserDomain: `sandbox.${NON_FUZZY_ANET_USER_DOMAIN}`,
  },
  clientsandbox: {
    userNamespace: '.clientsandbox.athenahealth.com',
    anetUserDomain: `clientsandbox.${NON_FUZZY_ANET_USER_DOMAIN}`,
  },
};

export const ANET_USER_PREFIX_SUPPORT_ENV_MAP: Record<string, string> = {
  p: 'preview',
  d: 'slsdemo',
  c: 'clienttrain',
  s: 'sandbox',
  cs: 'clientsandbox',
};

export const PREPROD_ANET_USER_DOMAINS = [
  `ptest.${NON_FUZZY_ANET_USER_DOMAIN}`,
  `dtest.${NON_FUZZY_ANET_USER_DOMAIN}`,
  `mtest.${NON_FUZZY_ANET_USER_DOMAIN}`,
  `candstg.${NON_FUZZY_ANET_USER_DOMAIN}`,
];

export const MASKED_ANET_USER_DOMAINS = [
  `prodma.${NON_FUZZY_ANET_USER_DOMAIN}`,
  `devma.${NON_FUZZY_ANET_USER_DOMAIN}`,
  `candma.${NON_FUZZY_ANET_USER_DOMAIN}`,
];

export const ATHENISTA_USER_DOMAIN = '@athenahealth.com';

export const SCIM_ANET_USER_DOMAIN_PATTERN = /\.scim\.(?:[^.]+\.)?athenahealth\.com$/i;

export const OHSW_EVENT_PREFIX = 'oktaHostedLogin::';
export const PASSWORD_EXPIRED_EVENT_PREFIX = 'PasswordExpired::';
export const PASSWORD_EXPIRY_REASON_REMEDIATION = 'password_expiry_remediation';
export const PASSWORD_EXPIRY_REASON_TEMP_PASSWORD = 'user_with_temp_password';
export const ERROR_PAGE_EVENT_PREFIX = 'ErrorPage::';
export const MFA_ENROLL_EVENT_PREFIX = 'MFAEnroll::';
export const MFA_CHALLENGE_EVENT_PREFIX = 'MFAChallenge::';

export const CLIENTID_ATHENAONE_HOST_MAPPING: Record<string, string[]> = {
  '0oaet0rfjNzyKCiQQ296': [ 'athenanet.athenahealth.com', 'devro.athenahealth.com' ],
  '0oa16evw7trKLV80X297': [ 'preview.athenahealth.com' ],
  '0oa1g3pp3dNEa1Rhm297': [ 'salesdemo.athenahealth.com' ],
  '0oa1g3qnz9iqZWIjR297': [ 'clienttrain.athenahealth.com' ],
  '0oa1g3qt4lLFlpa7d297': [ 'sandbox.athenahealth.com' ],
  '0oa4zsgxk7LR1gMAo297': [ 'clientsandbox.athenahealth.com' ],
};

export type LanguageType = 'en' | 'es';
function _getManualInstructionsText(appName: string, language: LanguageType): string {
  if (language === 'en') {
    return `Open the ${appName} app on your mobile device and follow the app's instructions to add an account. For Account Type select "Other", then choose "Enter Key Manually". Provide any label in the Account Name field and enter the following in the Key field:`;
  }
  return `Abre la ${appName} aplicación en su dispositivo móvil y sigue las instrucciones de la aplicación para agregar una cuenta. Para el Tipo de cuenta, selecciona “Otro” y luego elige “Ingresar clave manualmente”. Introduce cualquier etiqueta en el campo Nombre de cuenta e ingresa lo siguiente en el campo Clave:`;
}
const TOTP_ENROLL_STEP_1_HEADER_TEXT_EN = 'Step 1 - Download app and add account';
const TOTP_ENROLL_STEP_1_HEADER_TEXT_ES = 'Paso 1: Descarga la aplicación y agrega una cuenta';
const TOTP_ENROLL_STEP_1_HEADER_DESCRIPTION_EN = 'Download a mobile app of your choice that supports time-based one-time passcodes (TOTP). For example, we support:';
const TOTP_ENROLL_STEP_1_HEADER_DESCRIPTION_ES = ' Descarga una aplicación móvil de tu elección que permita el uso de contraseñas de un solo uso basadas en el tiempo (TOTP). Por ejemplo, aceptamos:';
const TOTP_ENROLL_STEP_2_HEADER_TEXT_EN = 'Step 2 - Enter the one-time passcode';
const TOTP_ENROLL_STEP_2_HEADER_TEXT_ES = 'Paso 2 - Ingresa el código de acceso de un solo uso';
const TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_EN = 'Enter the one-time passcode displayed in the app.';
const TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_ES = 'Ingresa el código de acceso de un solo uso que se muestra en la aplicación.';

const BARCODE_TOTP_CANT_SCAN_BUTTON_TEXT_EN = 'Can\'t scan? Enter a setup key instead';
const BARCODE_TOTP_CANT_SCAN_BUTTON_TEXT_ES = '¿Puedes escanear? Ingresa una clave de configuración en su lugar';
const BARCODE_TOTP_MANUAL_SECRET_KEY_INSTRUCTIONS_TEXT_EN = 'Open the app on your mobile device and add an account by entering the setup key below:';
const BARCODE_TOTP_MANUAL_SECRET_KEY_INSTRUCTIONS_TEXT_ES = 'Abre la aplicación en tu dispositivo móvil y agrega una cuenta ingresando la clave de seguridad a continuación:';

const BACK_TO_FACTORS_TEXT_EN = 'Choose a different method';
const BACK_TO_FACTORS_TEXT_ES = 'Elige un método diferente';
const PRIMARY_AUTH_SUBMIT_TEXT_EN = 'Log In';
const PRIMARY_AUTH_SUBMIT_TEXT_ES = 'Iniciar sesión';
const OKTA_VERIFY_APP = 'Okta Verify';
const GOOGLE_AUTHENTICATOR_MANUAL_INSTRUCTIONS_TEXT_EN = _getManualInstructionsText('Google Authenticator', 'en');
const OKTA_VERIFY_MANUAL_INSTRUCTIONS_TEXT_EN = _getManualInstructionsText('Okta Verify', 'en');
const GOOGLE_AUTHENTICATOR_MANUAL_INSTRUCTIONS_TEXT_ES = _getManualInstructionsText('Google Authenticator', 'es');
const OKTA_VERIFY_MANUAL_INSTRUCTIONS_TEXT_ES = _getManualInstructionsText('Okta Verify', 'es');
const SEND_CODE_TEXT_EN = 'Send Code';
const SEND_CODE_TEXT_ES = 'Enviar código';

const TOTP_DESCRIPTION_TEXT_EN = 'Use a mobile app (Google Authenticator, Microsoft Authenticator, Duo Mobile, etc.) to get a one-time passcode.';
const TOTP_DESCRIPTION_TEXT_ES = 'Utiliza una aplicación móvil (Google Authenticator, Microsoft Authenticator, Duo Mobile, etc.) para obtener un código de acceso de un solo uso.';
const TOTP_FACTOR_NAME = 'Authenticator App (TOTP)';
const ENROLL_CHOICES_TITLE_EN = 'Set up at least 1 authentication method';
const ENROLL_CHOICES_TITLE_ES = 'Configurar al menos 1 método de autenticación';

const ENROLL_CHOICES_DESCRIPTION_EN = 'To protect your account, tell us how to verify it’s you when additional authentication is required.';
const ENROLL_CHOICES_DESCRIPTION_ES = 'Para proteger tu cuenta, dinos cómo verificar que eres tú cuando se requiera autenticación adicional.';
const ENROLL_CHOICES_OPTIONAL_TITLE_EN = 'Set up another authentication method';
const ENROLL_CHOICES_OPTIONAL_TITLE_ES = 'Configurar otro método de autenticación';

const OKTA_VERIFY_APP_DESCRIPTION_EN = 'Use the Okta Verify mobile app to get a one-time passcode or push notification.';
const OKTA_VERIFY_APP_DESCRIPTION_ES = 'Utiliza la aplicación móvil Okta Verify para obtener un código de acceso de un solo uso o una notificación push.';

export const ENROLLED_FACTOR_CONFIG = {
  okta_verify: { iconClass: 'mfa-okta-verify', labelI18nKey: 'oie.okta_verify.label', descriptionI18nKey: '' },
  google_otp: { iconClass: 'mfa-google-auth', labelI18nKey: 'oie.authenticatorapp.label', descriptionI18nKey: '' },
  phone_number: { iconClass: 'mfa-okta-phone', labelI18nKey: 'oie.phone.label', descriptionI18nKey: '' },
  security_question: { iconClass: 'mfa-okta-security-question', labelI18nKey: 'oie.security.question.label', descriptionI18nKey: '' },
  webauthn: { iconClass: 'mfa-webauthn', labelI18nKey: 'oie.webauthn.label', descriptionI18nKey: 'oie.webauthn.description' },
};

const OKTA_VERIFY = 'Okta Verify';
const OKTA_VERIFY_SETUP_EN = `${OKTA_VERIFY} Setup`;
const OKTA_VERIFY_SETUP_ES = `${OKTA_VERIFY} Configurar`;

export const I18N_MAPPING = {
  en: {
    'enroll.totp.title': '{0} Setup',
    'enroll.choices.setup': 'Set Up',
    'enroll.choices.list.enrolled': 'Your Enrolled Methods',
    'enroll.choices.list.optional': 'Other Available Methods',
    'enroll.choices.optional.title': ENROLL_CHOICES_OPTIONAL_TITLE_EN,
    'enroll.choices.optional': ENROLL_CHOICES_OPTIONAL_TITLE_EN,
    'enroll.choices.optional.description': 'Set up backup authentication methods for your account, or continue with your enrolled methods.',
    'enroll.choices.description.generic': ENROLL_CHOICES_DESCRIPTION_EN,
    'enroll.choices.title': ENROLL_CHOICES_TITLE_EN,
    'errors.E0000047': 'Service overloaded, please try again later.',
    'error.fido2skipenrollment.header': 'Something went wrong',
    'error.fido2skipenrollment.description.part1': "We ran into a temporary issue and couldn't process your request. Click ",
    'error.fido2skipenrollment.description.dismiss': 'Dismiss',
    'error.fido2skipenrollment.description.part2': ' below to keep your existing methods, set up a new authentication method or click on back to login to continue.',
    'enroll.totp.setupApp': "Open the {0} app on your mobile device and follow the app's instructions to add an account.",
    'enroll.totp.cannotScanBarcode': ' ',
    'oie.enroll.google_authenticator.cannotScanBarcode.title': ' ',
    'enroll.totp.setupManually': 'Set up manually without push notification',
    'enroll.totp.enrollViaSms.title': OKTA_VERIFY_SETUP_EN,
    'factor.oktaVerifyPush': OKTA_VERIFY_APP,
    'factor.push.description': OKTA_VERIFY_APP_DESCRIPTION_EN,
    'factor.totpSoft.oktaVerify': OKTA_VERIFY_APP,
    'factor.totpSoft.description': TOTP_DESCRIPTION_TEXT_EN,
    'factor.totpSoft.googleAuthenticator': TOTP_FACTOR_NAME,
    'mfa.backToFactors': BACK_TO_FACTORS_TEXT_EN,
    'enroll.totp.downloadApp': 'Download <a href="{0}" class="inline-link">{1} from the {2}</a> on your mobile device.',
    'mfa.phoneNumber.placeholder': 'Phone Number',
    'mfa.scanBarcode': 'Scan QR Code',
    goback: 'Back to login',
    'primaryauth.submit': PRIMARY_AUTH_SUBMIT_TEXT_EN,
    'oie.primaryauth.submit': PRIMARY_AUTH_SUBMIT_TEXT_EN,
    'errors.E0000004': 'Invalid Username or Password',
    'errors.E0000119': 'Your account is locked. Please contact your administrator.',
    'enroll.call.setup': 'Voice Call Setup',
    'enroll.sms.setup': 'Text Message (SMS) Setup',
    'oie.phone.enroll.call.subtitle': 'Receive a code via phone call to finish setup',
    'oie.phone.enroll.sms.subtitle': 'Receive a code via SMS to finish setup',
    'oie.phone.sms.primaryButton': SEND_CODE_TEXT_EN,
    'mfa.sendCode': SEND_CODE_TEXT_EN,
    'mfa.resendCode': 'Re-Send Code',
    'oie.phone.call.primaryButton': 'Call',
    'oie.enroll.switch.authenticator': BACK_TO_FACTORS_TEXT_EN,
    'oie.enroll.google_authenticator.manualSetupInstructions': BARCODE_TOTP_MANUAL_SECRET_KEY_INSTRUCTIONS_TEXT_EN,
    'enroll.totp.manualSetupInstructions.generic': GOOGLE_AUTHENTICATOR_MANUAL_INSTRUCTIONS_TEXT_EN,
    'enroll.totp.sharedSecretInstructions.generic': OKTA_VERIFY_MANUAL_INSTRUCTIONS_TEXT_EN,
    'oie.okta_verify.label': OKTA_VERIFY_APP,
    'oie.verification.switch.authenticator': BACK_TO_FACTORS_TEXT_EN,
    'oie.optional.authenticator.button.title': 'Finish',
    'oie.verify.google_authenticator.otp.title': TOTP_FACTOR_NAME,
    'oie.okta_verify.authenticator.description': OKTA_VERIFY_APP_DESCRIPTION_EN,
    'factor.sms': 'Text Message (SMS)',
    'oie.select.authenticators.enroll.title': ENROLL_CHOICES_TITLE_EN,
    'oie.select.authenticators.enroll.subtitle': ENROLL_CHOICES_DESCRIPTION_EN,
    'factor.call': 'Voice Call',
    'factor.call.description': 'We’ll call you with a one-time passcode.',
    'factor.sms.description': 'We’ll text you a one-time passcode.',
    'oie.enroll.okta_verify.channel.sms.description': 'Make sure you can access the text on your mobile device.',
    'oie.phone.enroll.title': 'Phone Setup',
    'oie.enroll.okta_verify.setupLink': 'Send Setup Link',
    'oie.phone.enroll.sms.label': 'Text message (SMS)',
    'oie.enroll.google_authenticator.setup.title': TOTP_ENROLL_STEP_1_HEADER_TEXT_EN,
    'oie.select.authenticator.enroll.okta_verify.authenticator.label': 'Okta Verify',
    'oie.enroll.okta_verify.setup.title': OKTA_VERIFY_SETUP_EN,
    'oie.phone.authenticator.description': 'We’ll text or call you with a one-time passcode.',
    'enroll.totp.cannotScan': 'Can’t scan?',
    'oie.okta_verify.push.title': 'Okta Verify',
    'oie.okta_verify.totp.title': 'Okta Verify',
    'oie.enroll.okta_verify.setup.sms.title': OKTA_VERIFY_SETUP_EN,
    'oie.phone.verify.mfa.calling.without.phone': 'Calling your phone. Enter the code below to finish setup.',
    'oie.phone.verify.sms.codeSentText.without.phone': 'A code was sent to your phone. Enter the code below to finish setup.',
    'oform.back': 'Go back',
    'oie.enroll.okta_verify.qrcode.cannotScan': "Can't scan? Use a setup link instead",
    'oie.enroll.google_authenticator.scanBarcode.cannotScan': BARCODE_TOTP_CANT_SCAN_BUTTON_TEXT_EN,
    'oie.phone.verify.title': 'Phone Verification',
    'errors.E0000068': 'Your code is invalid or expired. Please try again.',
    'oie.enroll.okta_verify.enroll.channel.sms.title': OKTA_VERIFY_SETUP_EN,
    'oie.enroll.okta_verify.select.channel.title': OKTA_VERIFY_SETUP_EN,
    'oie.google_authenticator.authenticator.description': TOTP_DESCRIPTION_TEXT_EN,
    'oie.enroll.google_authenticator.scanBarcode.title': TOTP_ENROLL_STEP_1_HEADER_DESCRIPTION_EN,
    'oie.enroll.google_authenticator.enterCode.title': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_EN,
    'oie.verify.google_authenticator.otp.description': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_EN,
    iphone: 'iOS',
    'registration.signup.label': "Don't have an account?",
    'registration.signup.text': 'Sign up',
    'error.athena.go.back.to.login': 'Go back to login.',
    'email.token.user.exists.warning': 'You already have an account using this email address. Please log in to continue.',
    'temp.password.hint': 'Please enter the temporary password provided by your healthcare provider.',
    'portal.degradedstate.header': 'Having trouble logging in?',
    'portal.degradedstate.description': 'We\'re having technical problems right now, and we\'re working to fix them. We apologize for this inconvenience. Please try logging in again later.',
    'primaryauth.athenanet.tandc': 'Your access and use of applications with this account is subject to the Terms of Use and Privacy Policy applicable to and found within those applications.',
    forgotpassword: 'Forgot Password?',
    'accountreset.button': 'Reset Account',
    'accountreset.error.link': 'click here to reset your password.',
    'accountreset.error': 'We’re sorry, something went wrong. To recover your account, please ',
    'accountreset.link': 'Cannot verify? Reset your account',
    'accountreset.loading': 'This may take a few minutes. Please do not close your browser or navigate away from this page.',
    'accountreset.loading.title': 'Resetting your account...',
    'accountreset.warning': 'Resetting your account will require you to set up a new password and verification method. To regain access to your health records, you may also need to verify access to your phone number on file with your healthcare provider.',
    'accountreset.warning.title': 'Are you sure?',
    cancel: 'Cancel',
    and: ' and ',
    'account.demographics.email': 'Email address',
    'error.athena.click.to.logout': 'Click here to log out.',
    'footer.privacy': 'Privacy Policy',
    'footer.termsofuse': 'Terms of Use',
    'oie.password.confirmPasswordLabel': 'Re-enter new password',
    'password.create.heading': 'Create your new password',
    'password.create.setpassword.description': 'Your athenahealth account will be your universal login credential across participating healthcare providers and applications. To get started, set your password below.',
    'password.create.tandcstart': 'By clicking Save and Continue, you agree to the ',
    'password.reset.environment.error.header': 'Incorrect Environment',
    'password.reset.environment.error': 'You are logged in as a Production user and cannot update your password from this support environment. To update your password, please go to',
    'password.reset.environment.athenaone.production': 'athenaOne Production',
    'password.edit.heading': 'Edit your password',
    'password.expiry.warning.heading': 'Update your password',
    'password.expiry.warning.description': 'Your current password no longer meets the updated requirements.',
    'password.requirements.checklist.header': 'Password Requirements',
    'password.requirement.fulfilled': 'All requirements met',
    'password.requirement.length': 'Between 8 and 72 characters',
    'password.requirement.lower': 'Have at least 1 lowercase letter',
    'password.requirement.number': 'Have at least 1 number',
    'password.requirement.upper': 'Have at least 1 uppercase letter',
    'primaryauth.tandcend': 'is subject to the Terms of Use and Privacy Policy applicable to and found within this application.',
    'primaryauth.tandcstart': 'Your access and use of',
    'save.and.continue': 'Save and Continue',
    'oie.enroll.okta_verify.setup.text': 'Okta Verify Setup',
    'idx.session.expired': 'You have been logged out due to inactivity. Refresh or return to the sign in screen.',
    'mfa.athena.scanBarcode': 'Use QR code instead',
    'errors.E0000005': 'Invalid session',
    'errors.E0000011': 'Invalid token provided',
    'footer.mfa.adminText': 'Please contact your administrator if you need help setting up or managing authentication methods.',
    'admin.coverage.default': 'please contact your administrator.',
    'admin.coverage.no.admin': "please call athenahealth's CSC at (800) 981-5086",
    'admin.coverage.support.instructions.prefix': "refer to your practice's support instructions below:",
    'challenge.okta.verify.totp.factor.description': 'Enter a code',
    'challenge.okta.verify.push.factor.description': 'Get a push notification',
    'okta.verify.setup.instructions': 'Download the Okta Verify app on your device (from the App Store on iOS, or from Google Play on Android), then open the app and follow the instructions to add your account. When prompted, scan the QR code below:',
    'totp.enroll.step.1.header.text': TOTP_ENROLL_STEP_1_HEADER_TEXT_EN,
    'totp.enroll.step.1.header.description': TOTP_ENROLL_STEP_1_HEADER_DESCRIPTION_EN,
    'totp.enroll.step.2.header.text': TOTP_ENROLL_STEP_2_HEADER_TEXT_EN,
    'totp.enroll.step.2.header.description': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_EN,
    'barcode.totp.cant.scan.text': BARCODE_TOTP_CANT_SCAN_BUTTON_TEXT_EN,
    'barcode.totp.manual.secret.key.instructions.text': BARCODE_TOTP_MANUAL_SECRET_KEY_INSTRUCTIONS_TEXT_EN,
    'mfa.verify.totp.header.description': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_EN,
    'impersonated.username.field.label': 'Impersonated Username',
    'invalid.username.password.description': 'If you don’t remember your password, ',
    'invalid.username.password.link.text': 'reset it now',
    'logout.banner.header': 'You are now logged out',
    'logout.banner.description': 'You have successfully ended your athenaOne login session.',
    'timedout.banner.header': 'Session Timed Out',
    'timedout.banner.description': 'Your session has timed out. Please log in to resume this session.',
    'refreshtimeout.banner.header': 'Refresh Timed Out',
    'refreshtimeout.banner.description': 'Your session has timed out. Please log in to start a new session.',
    'conciergemode.banner.header': 'Session Locked',
    'conciergemode.banner.description': 'Your session has been locked while the patient creates an account using your device. If the patient registration workflow does not open automatically in a new tab, you may need to disable your browser\'s pop-up blocker. Please log in to resume this session.',
    'sessionlocked.banner.header': 'Multiple User Sessions',
    'sessionlocked.banner.description': 'Your session has been locked because {0} logged in through the same browser. Please log in to resume this session.',
    'logintimedout.banner.header': 'Login Timed Out',
    'logintimedout.banner.description': 'Your session has timed out during the login process. Please log in again.',
    'athenistasssowarning.banner.header': 'Sign-In Instructions',
    'athenistasssowarning.banner.description.part1': 'If you\'re an athenista, please use ',
    'athenistasssowarning.banner.description.part2': ' to login. Bookmark this URL for future use.',
    'error.default.header': 'Login Failed',
    'error.default.description': 'We\'re sorry, something went wrong. Please try again later.',
    'error.accountexpired.header': 'Account Blocked',
    'error.accountexpired.description': 'Your account is blocked. Please contact your system administrator to regain access.',
    'error.accountblockedshelluser.header': '',
    'error.accountblockedshelluser.description': 'You cannot log in with this username. Please try a different username.',
    'error.accountblocked.header': 'Account Blocked',
    'error.accountblocked.description': 'Your account is blocked. Please contact your system administrator to regain access.',
    'error.accountblocked.degraded.header': 'Account Blocked',
    'error.accountblocked.degraded.description': 'Your account is blocked, and password reset is temporarily unavailable. Please try again later.',
    'error.accountblocked.lwa.header': 'Account Locked',
    'error.accountblocked.lwa.description.part1': 'Your account is locked. To unlock your account, ',
    'error.accountblocked.lwa.description.link': 'click here',
    'error.accountblocked.lwa.description.part2': ' to reset your password.',
    'error.timeofdayrestricted.header': '',
    'error.timeofdayrestricted.description': '{0}',
    'error.unknownidentityprovider.header': 'Single Sign-On Failed',
    'error.unknownidentityprovider.description': 'You logged in using an identity provider {0} that is not configured for single sign-on. Please contact your system administrator to confirm your identity provider.',
    'error.epcsidpnotallowed.header': 'Single Sign-On Failed',
    'error.epcsidpnotallowed.description': 'You logged in using the identity provider {0}, which is configured for EPCS use only. Please contact your system administrator to confirm the appropriate identity provider for logging into athenaOne.',
    'error.unknownanetuser.header': 'No Associated Account',
    'error.unknownanetuser.description': 'Your username {0} is not associated with an athenaOne account in this environment. Please contact your system administrator for assistance.',
    'error.wronguser.header': 'Session Lost',
    'error.wronguser.description': 'The current session cannot be resumed. Please log in again to start a new session.',
    'error.accountmappingerrormultiple.header': 'Multiple Accounts',
    'error.accountmappingerrormultiple.description': 'Your external username {0} is mapped to multiple athenaOne accounts {1} but can only be mapped to one.  Please contact your system administrator for assistance.',
    'error.accountmappingerrorempty.header': 'No Associated Account',
    'error.accountmappingerrorempty.description': 'Your external username {0} is not associated with an athenaOne account. Please contact your system administrator for assistance.',
    'error.autocreationerror.header': 'Auto-Provisioning Failed',
    'error.autocreationerror.description': 'Could not create an athenaOne account for single sign-on.',
    'error.badtoken.header': '',
    'error.badtoken.description': '{0}',
    'error.transienterror.header': 'Login Interrupted',
    'error.transienterror.description': 'We’re sorry, something went wrong. Please restart your browser and try again.',
    'error.accessdenied.header': 'Access Denied',
    'error.accessdenied.description': 'You are logged in as someone who does not have access to athenaOne (e.g., a patient). Please log out and try again.',
    'error.temporarilyunavailable.header': 'Login Unavailable',
    'error.temporarilyunavailable.description': 'Login service could not be reached. Please try again later.',
    'error.invalididentityprovider.header': 'Single Sign-On Failed',
    'error.invalididentityprovider.description': 'You clicked a single sign-on link for an identity provider that does not exist or is not configured for single sign-on. Please contact your system administrator to update the single sign-on link.',
    'error.toomanyrequests.header': 'Too Many Requests',
    'error.toomanyrequests.description': 'Service is overloaded. Please try again later.',
    'error.nopractice.header': 'No Practice Access',
    'error.nopractice.description': 'Your account is not associated with any practices. Please contact your system administrator for assistance.',
    'error.terminalnotallowed.header': 'IP Address Blocked',
    'error.terminalnotallowed.description': 'You are not allowed to log in from terminal {0}.',
    'error.acccountblockedmaxlogins.header': 'Account Blocked',
    'error.acccountblockedmaxlogins.description': 'You have exceeded the maximum number of login attempts. Please contact your system administrator to regain access.',
    'oie.authenticatorapp.label': TOTP_FACTOR_NAME,
    'oie.security.question.label': 'Security Question',
    'oie.phone.label': 'Phone',
    'configure.browser.text': 'Configure Browser',
    'introduce.colleague.text': 'Introduce Us to a Colleague',
    'oie.enroll.okta_verify.sms.info.beginning': 'We sent an SMS to ',
    'oie.enroll.okta_verify.sms.info.end': ' with an Okta Verify setup link. To continue, open the link on your mobile device.',
    'error.invalididentitymapping.header': 'Single Sign-On Failed',
    'error.invalididentitymapping.description': 'The account you used to log in to your identity provider is not mapped to an athenaOne account. Please contact your system administrator for help.',
    'error.resetpasswordblockedunlockeduser.header': 'Account Blocked',
    'error.resetpasswordblockedunlockeduser.description': 'Your account is blocked. Please reset your password to unblock your account.',
    'error.resetpasswordblockeduser.header': 'Account Blocked',
    'error.resetpasswordblockeduser.description': 'Your account is blocked. Please reset your password to unblock your account.',
    'error.authserveraccessdenied.header': 'Account Blocked',
    'error.authserveraccessdenied.description': 'Your account is blocked. Please contact your system administrator to regain access.',
    'password.length.requirements.not.met': 'Password does not meet length requirements',
    'oie.webauthn.label': 'Passkey (FIDO2)',
    'oie.webauthn.description': 'Use your device’s biometrics (Windows Hello, FaceID, etc.) or a physical security key (USB or Bluetooth).',
    'oie.enroll.webauthn.title': 'Passkey (FIDO2) Setup',
    'oie.browser.error.NotAllowedError': 'The operation either timed out or was not allowed. Please try again.',
    'oie.enroll.webauthn.save': 'Set Up',
    'oie.webauthn.error.not.supported': 'Passkey (FIDO2) is not supported on this browser. Contact your admin for assistance.',
    'oie.verify.webauth.title': 'Passkey (FIDO2) Verification',
    'oie.setup.remind.me.later': 'Dismiss (keep existing methods)',
    'webauthn.try.again': 'Try Again',
    retry: 'Retry',
    'webauthn.enroll.description': 'You will be prompted to register a security key or biometric authenticator. For example, we support:',
    'webauthn.enroll.supported.method.phone': 'Use a phone or tablet',
    'webauthn.enroll.supported.method.icloud': 'iCloud Keychain',
    'webauthn.enroll.supported.method.chrome': 'Chrome profile',
    'webauthn.enroll.supported.method.windows.hello': 'Windows Hello',
    'webauthn.enroll.supported.method.usb.key': 'USB security key',
    'webauthn.enroll.follow.instructions': 'Follow the instructions on your browser to complete set up.',
    'webauthn.new.factor.banner.header': 'New Factor Available',
    'webauthn.new.factor.banner.body': "We've added a new authentication option: Passkey (FIDO2). Learn more in our ",
    'webauthn.new.factor.banner.link': 'Release Notes.',
    'webauthn.verify.biometric.title': 'Fingerprint, face recognition, or PIN',
    'webauthn.verify.biometric.description': 'Use the same device where you set it up.',
    'webauthn.verify.security.key.title': 'Security key',
    'webauthn.verify.security.key.description': 'Insert it into a USB port. When prompted by the browser, tap the button or gold disk. Security keys may work on multiple devices.',
  },
  es: {
    'errors.E0000047': 'El servicio esta sobrecargado. Inténtalo de nuevo más tarde.',
    'error.fido2skipenrollment.header': 'Something went wrong',
    'error.fido2skipenrollment.description.part1': "We ran into a temporary issue and couldn't process your request. Click ",
    'error.fido2skipenrollment.description.dismiss': 'Dismiss',
    'error.fido2skipenrollment.description.part2': ' below to keep your existing methods, set up a new authentication method or click on back to login to continue.',
    'registration.signup.label': '\u00BFNo tiene una cuenta?',
    'registration.signup.text': 'Reg\u00EDstrese',
    'error.athena.go.back.to.login': 'Regresar para iniciar sesión.',
    'email.token.user.exists.warning': 'Ya tienes una cuenta con esta dirección de email. Inicia sesión para continuar.',
    'temp.password.hint': 'Ingrese la contraseña temporal proporcionada por su proveedor de atención médica.',
    'portal.degradedstate.header': '¿Tienes problemas para iniciar sesión?',
    'portal.degradedstate.description': 'En este momento tenemos problemas técnicos y estamos trabajando para solucionarlos. Te pedimos disculpas por este inconveniente. Intenta iniciar sesión de nuevo más tarde.',
    'primaryauth.athenanet.tandc': 'Tu acceso y uso de las aplicaciones con esta cuenta está sujeto a las condiciones de uso y a la política de privacidad aplicables y que se encuentran en dichas aplicaciones.',
    forgotpassword: '¿Olvidó la contraseña?',
    'accountreset.button': 'Restablecer cuenta',
    'accountreset.error.link': 'inicia un restablecimiento de contraseña haciendo clic aquí.',
    'accountreset.error': 'Lo sentimos, algo salió mal. Para recuperar tu cuenta, por favor ',
    'accountreset.link': '¿No puedes verificarla? Restablece tu cuenta',
    'accountreset.loading': 'Esto puede tardar unos minutos. No cierres el navegador ni salgas de esta página.',
    'accountreset.loading.title': 'Restablecer tu cuenta...',
    'accountreset.warning': 'Para restablecer tu cuenta, deberás configurar una nueva contraseña y un nuevo método de verificación. Para volver a tener acceso a tu historial médico, es posible que también tengas que verificar el acceso a tu número de teléfono que consta en los archivos de tu proveedor de asistencia sanitaria.',
    'accountreset.warning.title': '¿Estás seguro?',
    goback: 'Regresar a inicio de sesión',
    cancel: 'Cancelar',
    and: ' y ',
    'account.demographics.email': 'Correo electrónico',
    'error.athena.click.to.logout': 'Haga clic aqui para salir.',
    'footer.privacy': 'Política de Privacidad',
    'footer.termsofuse': 'Términos de uso',
    'oie.password.confirmPasswordLabel': 'Vuelve a ingresar la nueva contraseña',
    'password.create.heading': 'Cree su nueva contraseña',
    'password.create.setpassword.description': 'Tu cuenta de athenahealth será tu credencial de inicio de sesión universal para todos los proveedores de atención médica participantes y aplicaciones. Para comenzar, configura tu contraseña a continuación.',
    'password.create.tandcstart': 'Al hacer clic en Guardar y Continuar, aceptas los ',
    'password.reset.environment.error.header': 'Incorrect Environment',
    'password.reset.environment.error': 'You are logged in as a Production user and cannot update your password from this support environment. To update your password, please go to',
    'password.reset.environment.athenaone.production': 'athenaOne Production',
    'password.edit.heading': 'Editar contraseña',
    'password.expiry.warning.heading': 'Actualiza tu contraseña',
    'password.expiry.warning.description': 'Tu contraseña actual ya no cumple con los requisitos actualizados.',
    'password.requirements.checklist.header': 'Requisitos de la contraseña',
    'password.requirement.fulfilled': 'Todos los requisitos cumplidos',
    'password.requirement.length': 'Tener entre 8 y 72 caracteres',
    'password.requirement.lower': 'Tener al menos 1 letra minúscula',
    'password.requirement.number': 'Tener al menos 1 número',
    'password.requirement.upper': 'Tener al menos 1 letra mayúscula',
    'primaryauth.tandcend': 'está sujeto a las condiciones de uso y a la política de privacidad aplicables y que se encuentran en esta aplicación.',
    'primaryauth.tandcstart': 'Tu acceso y uso de',
    'save.and.continue': 'Guardar y Continuar',
    'enroll.totp.title': 'Configuración de {0}',
    'enroll.choices.setup': 'Configuración',
    'enroll.choices.list.enrolled': 'Tus métodos registrados',
    'enroll.choices.list.optional': 'Otros métodos disponibles',
    'enroll.choices.optional.title': ENROLL_CHOICES_OPTIONAL_TITLE_ES,
    'enroll.choices.optional': ENROLL_CHOICES_OPTIONAL_TITLE_ES,
    'enroll.choices.optional.description': 'Configura métodos de autenticación de respaldo para tu cuenta o continúa con los métodos que ya tienes registrados.',
    'enroll.choices.description.generic': ENROLL_CHOICES_DESCRIPTION_ES,
    'enroll.choices.title': ENROLL_CHOICES_TITLE_ES,
    'enroll.totp.setupApp': 'Abre la {0} aplicación en su dispositivo móvil y sigue las instrucciones de la aplicación para agregar una cuenta.',
    'enroll.totp.cannotScanBarcode': ' ',
    'oie.enroll.google_authenticator.cannotScanBarcode.title': ' ',
    'enroll.totp.setupManually': 'Configurar manualmente sin notificación push',
    'enroll.totp.enrollViaSms.title': OKTA_VERIFY_SETUP_ES,
    'factor.oktaVerifyPush': OKTA_VERIFY_APP,
    'factor.push.description': OKTA_VERIFY_APP_DESCRIPTION_ES,
    'factor.totpSoft.oktaVerify': OKTA_VERIFY_APP,
    'factor.totpSoft.description': TOTP_DESCRIPTION_TEXT_ES,
    'factor.totpSoft.googleAuthenticator': TOTP_FACTOR_NAME,
    'mfa.backToFactors': BACK_TO_FACTORS_TEXT_ES,
    'enroll.totp.downloadApp': 'Descargar <a href="{0}" class="inline-link">{1} desde {2}</a> en su dispositivo móvil.',
    'mfa.phoneNumber.placeholder': 'Número de teléfono',
    'mfa.scanBarcode': 'Escanear c\u00F3digo QR',
    'primaryauth.submit': PRIMARY_AUTH_SUBMIT_TEXT_ES,
    'oie.primaryauth.submit': PRIMARY_AUTH_SUBMIT_TEXT_ES,
    'errors.E0000004': 'Nombre de usuario o contraseña inválidos.',
    'errors.E0000119': 'Su cuenta est\u00E1 bloqueada. Comun\u00EDquese con su administrador.',
    'enroll.call.setup': 'Configuración de llamadas de voz',
    'enroll.sms.setup': 'Configuración de mensajes de texto (SMS)',
    'oie.phone.enroll.call.subtitle': 'Configuración de llamada de voz',
    'oie.phone.enroll.sms.subtitle': 'Configuración de mensaje de texto (SMS)',
    'oie.phone.sms.primaryButton': SEND_CODE_TEXT_ES,
    'mfa.sendCode': SEND_CODE_TEXT_ES,
    'mfa.resendCode': 'Reenviar código',
    'oie.phone.call.primaryButton': 'Llamada',
    'oie.enroll.switch.authenticator': BACK_TO_FACTORS_TEXT_ES,
    'oie.enroll.google_authenticator.manualSetupInstructions': BARCODE_TOTP_MANUAL_SECRET_KEY_INSTRUCTIONS_TEXT_ES,
    'enroll.totp.manualSetupInstructions.generic': GOOGLE_AUTHENTICATOR_MANUAL_INSTRUCTIONS_TEXT_ES,
    'enroll.totp.sharedSecretInstructions.generic': OKTA_VERIFY_MANUAL_INSTRUCTIONS_TEXT_ES,
    'oie.okta_verify.label': OKTA_VERIFY_APP,
    'oie.verification.switch.authenticator': BACK_TO_FACTORS_TEXT_ES,
    'oie.optional.authenticator.button.title': 'Finalizar',
    'oie.verify.google_authenticator.otp.title': TOTP_FACTOR_NAME,
    'oie.okta_verify.authenticator.description': OKTA_VERIFY_APP_DESCRIPTION_ES,
    'factor.sms': 'Mensajes de texto (SMS)',
    'oie.select.authenticators.enroll.title': ENROLL_CHOICES_TITLE_ES,
    'oie.select.authenticators.enroll.subtitle': ENROLL_CHOICES_DESCRIPTION_ES,
    'factor.call': 'Llamadas de voz',
    'factor.call.description': 'Te llamaremos con un código de acceso de un solo uso.',
    'factor.sms.description': 'Te enviaremos por mensaje de texto un código de acceso de un solo uso.',
    'oie.enroll.okta_verify.channel.sms.description': 'Revise que puede acceder al mensaje de texto en su dispositivo m\u00F3vil.',
    'oie.phone.enroll.title': 'Configuración del teléfono',
    'oie.enroll.okta_verify.setupLink': 'Enviar enlace de configuración',
    'oie.phone.enroll.sms.label': 'Mensajes de texto (SMS)',
    'oie.enroll.google_authenticator.setup.title': TOTP_ENROLL_STEP_1_HEADER_TEXT_ES,
    'oie.select.authenticator.enroll.okta_verify.authenticator.label': 'Okta Verify',
    'oie.enroll.okta_verify.setup.title': OKTA_VERIFY_SETUP_ES,
    'oie.phone.authenticator.description': 'Le enviaremos un mensaje de texto o le llamaremos con un código de acceso de un solo uso.',
    'enroll.totp.cannotScan': '¿No puedes escanear?',
    'oie.okta_verify.push.title': 'Okta Verify',
    'oie.okta_verify.totp.title': 'Okta Verify',
    'oie.enroll.okta_verify.setup.sms.title': OKTA_VERIFY_SETUP_ES,
    'oie.phone.verify.mfa.calling.without.phone': 'Llamando a tu teléfono. Ingresa el código a continuación para finalizar la configuración.',
    'oie.phone.verify.sms.codeSentText.without.phone': 'Se envió un código a tu teléfono. Ingresa el código a continuación para finalizar la configuración.',
    'oform.back': 'Regresar',
    'oie.enroll.okta_verify.qrcode.cannotScan': '¿No puedes escanear? Utiliza un enlace de configuración en su lugar',
    'oie.enroll.google_authenticator.scanBarcode.cannotScan': BARCODE_TOTP_CANT_SCAN_BUTTON_TEXT_ES,
    'oie.phone.verify.title': 'Verificación telefónica',
    'errors.E0000068': 'Tu código no es válido o ha expirado. Inténtalo de nuevo.',
    'oie.enroll.okta_verify.enroll.channel.sms.title': OKTA_VERIFY_SETUP_ES,
    'oie.enroll.okta_verify.select.channel.title': OKTA_VERIFY_SETUP_ES,
    'oie.google_authenticator.authenticator.description': TOTP_DESCRIPTION_TEXT_ES,
    'oie.enroll.google_authenticator.scanBarcode.title': TOTP_ENROLL_STEP_1_HEADER_DESCRIPTION_ES,
    'oie.enroll.google_authenticator.enterCode.title': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_ES,
    'oie.verify.google_authenticator.otp.description': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_ES,
    iphone: 'iOS',
    'idx.session.expired': 'Fue desconectado por inactividad. Actualice o regrese a la pantalla de inicio de sesi\u00F3n.',
    'mfa.athena.scanBarcode': 'Utiliza el código QR en su lugar',
    'errors.E0000005': 'Sesi\u00F3n no v\u00E1lida',
    'errors.E0000011': 'Se proporcion\u00F3 un token no v\u00E1lido',
    'footer.mfa.adminText': 'Comunicate con tu administrador si necesitas ayuda para configurar o administrar los métodos de autenticación.',
    'challenge.okta.verify.factor.description': 'Introduzca un c\u00F3digo',
    'challenge.okta.verify.totp.factor.description': 'Introduzca un c\u00F3digo',
    'challenge.okta.verify.push.factor.description': 'Obtener una notificaci\u00F3n push',
    'okta.verify.setup.instructions': 'Descarga la aplicación Okta Verify en tu dispositivo (desde la App Store en iOS o Google Play en Android), luego abre la aplicación y sigue las instrucciones para agregar tu cuenta. Cuando se te indique, escanea el siguiente código QR:',
    'totp.enroll.step.1.header.text': TOTP_ENROLL_STEP_1_HEADER_TEXT_ES,
    'totp.enroll.step.1.header.description': TOTP_ENROLL_STEP_1_HEADER_DESCRIPTION_ES,
    'totp.enroll.step.2.header.text': TOTP_ENROLL_STEP_2_HEADER_TEXT_ES,
    'totp.enroll.step.2.header.description': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_ES,
    'barcode.totp.cant.scan.text': BARCODE_TOTP_CANT_SCAN_BUTTON_TEXT_ES,
    'barcode.totp.manual.secret.key.instructions.text': BARCODE_TOTP_MANUAL_SECRET_KEY_INSTRUCTIONS_TEXT_ES,
    'mfa.verify.totp.header.description': TOTP_ENROLL_STEP_2_HEADER_DESCRIPTION_ES,
    'impersonated.username.field.label': 'Impersonated Username',
    'invalid.username.password.description': 'Si no recuerdas tu contraseña, ',
    'invalid.username.password.link.text': 'reiníciala ahora',
    'logout.banner.header': 'You are now logged out',
    'logout.banner.description': 'You have successfully ended your athenaOne login session.',
    'timedout.banner.header': 'La sesión ha expirado',
    'timedout.banner.description': 'Tu sesión ha expirado. Inicia sesión para reanudarla.',
    'refreshtimeout.banner.header': 'El tiempo de actualización ha expirado',
    'refreshtimeout.banner.description': 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo',
    'conciergemode.banner.header': 'Session Locked',
    'conciergemode.banner.description': 'Your session has been locked while the patient creates an account using your device. If the patient registration workflow does not open automatically in a new tab, you may need to disable your browser’s pop-up blocker. Please log in to resume this session.',
    'sessionlocked.banner.header': 'Multiple User Sessions',
    'sessionlocked.banner.description': 'Your session has been locked because {0} logged in through the same browser. Please log in to resume this session.',
    'logintimedout.banner.header': 'Login Timed Out',
    'logintimedout.banner.description': 'Your session has timed out during the login process. Please log in again.',
    'error.default.header': 'Login Failed',
    'error.default.description': 'We’re sorry, something went wrong. Please try again later.',
    'error.accountexpired.header': 'Cuenta bloqueada',
    'error.accountexpired.description': 'Your account is blocked. Please contact your system administrator to regain access.',
    'error.accountblockedshelluser.header': '',
    'error.accountblockedshelluser.description': 'You cannot log in with this username. Please try a different username.',
    'error.accountblocked.header': 'Cuenta bloqueada',
    'error.accountblocked.description': 'Tu cuenta está bloqueada. Comunícate con el administrador del sistema para recuperar el acceso.',
    'error.accountblocked.degraded.header': 'Cuenta bloqueada',
    'error.accountblocked.degraded.description': 'Su cuenta está obstruida y la opción de restablecer la contraseña no está disponible temporalmente. Inténtelo de nuevo más tarde.',
    'error.accountblocked.lwa.header': 'Cuenta bloqueada',
    'error.accountblocked.lwa.description.part1': 'Tu cuenta está bloqueada. Para desbloquear tu cuenta, ',
    'error.accountblocked.lwa.description.link': 'haz clic aquí',
    'error.accountblocked.lwa.description.part2': ' para restablecer tu contraseña.',
    'error.timeofdayrestricted.header': '',
    'error.timeofdayrestricted.description': '{0}',
    'error.unknownidentityprovider.header': 'Single Sign-On Failed',
    'error.unknownidentityprovider.description': 'You logged in using an identity provider {0} that is not configured for single sign-on. Please contact your system administrator to confirm your identity provider.',
    'error.epcsidpnotallowed.header': 'Single Sign-On Failed',
    'error.epcsidpnotallowed.description': 'You logged in using the identity provider {0}, which is configured for EPCS use only. Please contact your system administrator to confirm the appropriate identity provider for logging into athenaOne.',
    'error.unknownanetuser.header': 'No Associated Account',
    'error.unknownanetuser.description': 'Your username {0} is not associated with an athenaOne account in this environment. Please contact your system administrator for assistance.',
    'error.wronguser.header': 'Session Lost',
    'error.wronguser.description': 'The current session cannot be resumed. Please log in again to start a new session.',
    'error.accountmappingerrormultiple.header': 'Multiple Accounts',
    'error.accountmappingerrormultiple.description': 'Your external username {0} is mapped to multiple athenaOne accounts {1} but can only be mapped to one.  Please contact your system administrator for assistance.',
    'error.accountmappingerrorempty.header': 'No Associated Account',
    'error.accountmappingerrorempty.description': 'Your external username {0} is not associated with an athenaOne account. Please contact your system administrator for assistance.',
    'error.autocreationerror.header': 'Auto-Provisioning Failed',
    'error.autocreationerror.description': 'Could not create an athenaOne account for single sign-on.',
    'error.badtoken.header': '',
    'error.badtoken.description': '{0}',
    'error.transienterror.header': 'Login Interrupted',
    'error.transienterror.description': 'We\'re sorry, something went wrong. Please restart your browser and try again.',
    'error.accessdenied.header': 'Access Denied',
    'error.accessdenied.description': 'You are logged in as someone who does not have access to athenaOne (e.g., a patient). Please log out and try again.',
    'error.temporarilyunavailable.header': 'Login Unavailable',
    'error.temporarilyunavailable.description': 'Login service could not be reached. Please try again later.',
    'error.invalididentityprovider.header': 'Single Sign-On Failed',
    'error.invalididentityprovider.description': 'You clicked a single sign-on link for an identity provider that does not exist or is not configured for single sign-on. Please contact your system administrator to update the single sign-on link.',
    'error.toomanyrequests.header': 'Too Many Requests',
    'error.toomanyrequests.description': 'Service is overloaded. Please try again later.',
    'error.nopractice.header': 'No Practice Access',
    'error.nopractice.description': 'Your account is not associated with any practices. Please contact your system administrator for assistance.',
    'error.terminalnotallowed.header': 'IP Address Blocked',
    'error.terminalnotallowed.description': 'You are not allowed to log in from terminal {0}.',
    'error.acccountblockedmaxlogins.header': 'Cuenta bloqueada',
    'error.acccountblockedmaxlogins.description': 'You have exceeded the maximum number of login attempts. Please contact your system administrator to regain access.',
    'oie.authenticatorapp.label': TOTP_FACTOR_NAME,
    'oie.security.question.label': 'Pregunta de seguridad',
    'oie.phone.label': 'Tel\u00E9fono',
    'oie.enroll.okta_verify.setup.text': 'Configurar Okta Verify',
    'configure.browser.text': 'Configure Browser',
    'introduce.colleague.text': 'Introduce Us to a Colleague',
    'oie.enroll.okta_verify.sms.info.beginning': 'Enviamos un SMS a ',
    'oie.enroll.okta_verify.sms.info.end': ' con un enlace de configuraci\u00F3n de Okta Verify. Para continuar, abra el enlace en su dispositivo m\u00F3vil.',
    'error.invalididentitymapping.header': 'Single Sign-On Failed',
    'error.invalididentitymapping.description': 'The account you used is not mapped to the identity provider you used to login. Please contact your system administrator for help or wait and retry later.',
    'error.resetpasswordblockedunlockeduser.header': 'Cuenta bloqueada',
    'error.resetpasswordblockedunlockeduser.description': 'Su cuenta está bloqueada. Por favor, restablezca su contraseña para desbloquear su cuenta.',
    'error.resetpasswordblockeduser.header': 'Cuenta bloqueada',
    'error.resetpasswordblockeduser.description': 'Su cuenta está bloqueada. Por favor, restablezca su contraseña para desbloquear su cuenta.',
    'error.authserveraccessdenied.header': 'Cuenta bloqueada',
    'error.authserveraccessdenied.description': 'Su cuenta está bloqueada. Por favor, póngase en contacto con su administrador del sistema para recuperar el acceso.',
    'password.length.requirements.not.met': 'La contraseña no cumple con los requisitos de longitud',
    'oie.webauthn.label': 'Passkey (FIDO2)',
    'oie.webauthn.description': 'Use your device’s biometrics (Windows Hello, FaceID, etc.) or a physical security key (USB or Bluetooth).',
    'oie.enroll.webauthn.title': 'Passkey (FIDO2) Setup',
    'oie.browser.error.NotAllowedError': 'The operation either timed out or was not allowed. Please try again.',
    'oie.enroll.webauthn.save': 'Set Up',
    'oie.webauthn.error.not.supported': 'Passkey (FIDO2) is not supported on this browser. Contact your admin for assistance.',
    'oie.verify.webauth.title': 'Passkey (FIDO2) Verification',
    'oie.setup.remind.me.later': 'Dismiss (keep existing methods)',
    'webauthn.try.again': 'Try Again',
    retry: 'Retry',
    'webauthn.enroll.description': 'You will be prompted to register a security key or biometric authenticator. For example, we support:',
    'webauthn.enroll.supported.method.phone': 'Use a phone or tablet',
    'webauthn.enroll.supported.method.icloud': 'iCloud Keychain',
    'webauthn.enroll.supported.method.chrome': 'Chrome profile',
    'webauthn.enroll.supported.method.windows.hello': 'Windows Hello',
    'webauthn.enroll.supported.method.usb.key': 'USB security key',
    'webauthn.enroll.follow.instructions': 'Follow the instructions on your browser to complete set up.',
    'webauthn.new.factor.banner.header': 'New Factor Available',
    'webauthn.new.factor.banner.body': "We've added a new authentication option: Passkey (FIDO2). Learn more in our ",
    'webauthn.new.factor.banner.link': 'Release Notes.',
    'webauthn.verify.biometric.title': 'Fingerprint, face recognition, or PIN',
    'webauthn.verify.biometric.description': 'Use the same device where you set it up.',
    'webauthn.verify.security.key.title': 'Security key',
    'webauthn.verify.security.key.description': 'Insert it into a USB port. When prompted by the browser, tap the button or gold disk. Security keys may work on multiple devices.',
  },
};

export const TOTP_PROVIDER_AMPLITUDE_EVENT : Record<string, string> = {
  OKTA: 'OktaVerify',
  GOOGLE: 'GoogleAuthenticator',
};

export const AUTHENTICATOR_METHOD_KEY_AMPLITUDE_EVENT: Record<string, string> = {
  google_otp: 'GoogleAuthenticator',
  okta_verify: 'OktaVerify',
  phone_number: 'Phone',
  security_question: 'Question',
  webauthn: 'WebAuthn',
};

export const AUTH_SERVER_ID_ANET_SIGNED_JWT_KEY_MAP: any = {
  auset0ja9xZ2Hniep296: 'prod',             // primary
  ausu5cwvrZnWYasjS296: 'supportEnvPHI',    // Support PHI
  aus1eumlr5MD3rYcn297: 'supportEnvNonPHI', // Support Non PHI
};

export const FOOTER_LINK_AMPLITUDE_EVENT_SUFFIX: Record<string, string> = {
  'signout-link': 'BackToLoginButtonClick',
  cancel: 'BackToLoginButtonClick',
  'back-link': 'ChooseADifferentFactorButtonClick',
  switchAuthenticator: 'ChooseADifferentFactorButtonClick',
};

export const TERMS_OF_USE_LINK = 'https://www.athenahealth.com/resources/patient-facing-applications-terms-of-use';

export const PRIVACY_POLICY_LINK = 'https://www.athenahealth.com/resources/privacy-notice-for-patient-facing-applications';

export const PASSWORD_REQUIREMENTS = {
  LENGTH_LOWER_BOUND: 15,
  LENGTH_HIGHER_BOUND: 72,
};

export const INVALID_IDP_ERROR_BANNER_KEYS = [
  'UNKNOWNIDENTITYPROVIDER',
  'INVALIDIDENTITYPROVIDER',
  'INVALIDIDENTITYMAPPING',
];

export const API_REQUEST_RETRY_MIN_TIMEOUT = 2000;

export const CLIENT_ID_TO_SUPPORT_ENV_MAPPING: Record<string, string> = {
  '0oa16evw7trKLV80X297': 'preview',
  '0oa1g3pp3dNEa1Rhm297': 'slsdemo',
  '0oa1g3qt4lLFlpa7d297': 'sandbox',
  '0oa4zsgxk7LR1gMAo297': 'clientsandbox',
  '0oa1g3qnz9iqZWIjR297': 'clienttrain',
};
